import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'fleettrace.theme';

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private windows and blocked site data both throw here. The default is fine.
  }
  return 'system';
}

export interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Owns the theme: the `.dark` class on &lt;html&gt;, and the resolved value that JavaScript
 * reads.
 *
 * <p>The class must be on &lt;html&gt; rather than a React wrapper: Base UI portals mount to
 * document.body, and a class on a wrapper would leave every dialog and popover resolving
 * tokens against the light theme.
 *
 * <p><strong>This has to be a context, and the reason is a bug that hid for a while.</strong>
 * It was originally a bare hook holding `useState`, which looked fine because the part
 * everyone checks -- the CSS -- kept working: each caller had its own copy of the state,
 * but the toggle's copy still flipped the global class on &lt;html&gt;, so every colour on
 * the page changed. What silently did NOT change was anything driven by the `resolved`
 * value in JavaScript, because those components held their own stale copies and were
 * never re-rendered: the MapLibre basemap URL, and the Clerk appearance object. The map
 * stayed light in dark mode.
 *
 * <p>So the rule: theme state lives here once, and anything reading it in JS gets it from
 * this context. Consuming outside the provider throws rather than quietly forking again.
 */
export function ThemeProvider({ children }: PropsWithChildren) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => {
    const stored = readPreference();
    return stored === 'system' ? systemTheme() : stored;
  });

  // useLayoutEffect, not useEffect: the class must be on <html> before anything reads a
  // computed CSS variable in the same commit. An inline script in index.html handles the
  // very first paint; this covers every change after it.
  useLayoutEffect(() => {
    const apply = () => {
      const next: ResolvedTheme = preference === 'system' ? systemTheme() : preference;
      setResolved(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
    };
    apply();

    if (preference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage being unavailable must not break theming.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    // Loud on purpose. The failure this replaces was a second copy of the state that
    // kept the CSS working while every JS reader went stale -- almost invisible.
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return value;
}
